import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { UserLoginDTO } from '../../../Models/Login';
import './forgotPassword.css';
import '../../style/login.css';
import '../../style/base.css';
import '../../style/list.css';
import { useAlert } from '../ui/alertProvider.tsx'
import AuthContext from '../../store/authContext';
import { validateName } from '../../Utils/validation';
import ApiClient from '../../Services/ApiClient';
import { SiteSettings } from '../../../Models/SiteSettings';
import { postVisit } from '../../Services/trafficApi';

const Login: React.FC = () => {
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl');
    const { login, setRegistrationAccountType } = useContext(AuthContext);

    // account type: patient (users) or manager (site staff)
    const [accountType, setAccountType] = useState<'patient' | 'manager'>('patient');
    const [registerLabel, setRegisterLabel] = useState<string>('הרשמה למטופלים');
    const [regAvail, setRegAvail] = useState<{ canRegister: boolean; remaining?: number | null; reason?: string | null } | null>(null);
    const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
    const regAvailRef = useRef<typeof regAvail>(null);

    // keep ref in sync so handlers read latest value
    useEffect(() => { regAvailRef.current = regAvail; }, [regAvail]);

    // keep registerLabel derived from accountType (single source of truth)
    useEffect(() => {
        setRegisterLabel(accountType === 'patient' ? 'הרשמה למטופלים' : 'הרשמה למנהלים');
    }, [accountType]);

    useEffect(() => {
        console.debug('[Login] accountType changed', accountType);
        console.log('accountype is' + accountType);
        console.trace('[Login] accountType change trace');
        setRegisterLabel(accountType === 'patient' ? 'הרשמה למטופלים' : 'הרשמה למנהלים');
    }, [accountType]);

    // adjust available UI based on site settings
    useEffect(() => {
        if (!siteSettings) return;
        const patientLogin = siteSettings.enablePatientLogin !== false;
        const userLogin = siteSettings.enableUsersLogin !== false;
        // If only one login type is enabled, force accountType to that
        if (patientLogin && !userLogin) setAccountType('patient');
        else if (!patientLogin && userLogin) setAccountType('manager');
        // If neither enabled, keep current but form will be disabled and toggles hidden
    }, [siteSettings]);

    const updateAccountType = (newType: 'patient' | 'manager') => {
        console.debug('[Login] updateAccountType called', { newType, prev: accountType });
        console.trace('[Login] updateAccountType trace');
        setAccountType(newType);
    };
    const [focused, setFocused] = useState<'patient' | 'manager' | null>(null);

    // hide register button when site settings explicitly disable registration for the selected type
    const canRegisterForSelectedType = accountType === 'patient'
        ? !(siteSettings && siteSettings.canRegisterNewPatients === false)
        : !(siteSettings && siteSettings.canRegisterNewUsers === false);

    const handleRegisterClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
        if (e) e.preventDefault();
        const currentAvail = regAvail;

        if (accountType === 'manager') {

            if (siteSettings && siteSettings.canRegisterNewUsers === false) {
                showAlert('הרשמות הושבתו על ידי מנהל המערכת', 'error');
                return;
            }

            // If site-wide login is disabled, prevent registration
            if (siteSettings && siteSettings.enableUsersLogin === false) {
                showAlert('הרשמות הושבתו על ידי מנהל המערכת', 'error');
                return;
            }

        }
        else if (accountType === 'patient') {
            // If site explicitly disables patient registration, block
            if (siteSettings && siteSettings.canRegisterNewPatients === false) {
                showAlert('הרשמת מטופלים הושבתה על ידי מנהל האתר', 'error');
                return;
            }
            if (currentAvail && currentAvail.canRegister === false) {
                if (currentAvail.reason === 'registration_disabled') showAlert('Patient registration is disabled by site administrator', 'error');
                else if (currentAvail.remaining === 0) showAlert('Patient registration is not available: site has reached its user limit', 'error');
                else showAlert('Patient registration is not available at this time', 'error');
                return;
            }
        }
        try { if (setRegistrationAccountType) setRegistrationAccountType(accountType); } catch {}
        navigate('/register');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !password) {
            showAlert('הכנס בבקשה שם משתמש וסיסמא', 'error');
            return;
        }
        const nameErr = validateName(name);
        if (nameErr) {
            setFieldError(nameErr);
            showAlert(nameErr, 'error');
            return;
        }

        setLoading(true);
        try {
            const success = await login({ name, password } as UserLoginDTO);
            if (success) {
                    // report successful login
                    try { postVisit('login_success'); } catch {}
                showAlert('התחברת בהצלחה', 'success');
                try {
                    const tok = localStorage.getItem('token');
                    if (tok) {
                        const payload = JSON.parse(atob(tok.split('.')[1]));
                        const role = payload?.role || payload?.Role || (Array.isArray(payload?.roles) ? payload.roles[0] : null);
                        if (returnUrl) {
                            // navigate to the returnUrl after login
                            navigate(returnUrl);
                        } else if (role === 'Patient' || (Array.isArray(payload?.roles) && payload.roles.includes('Patient'))) {
                            navigate('/patient/meetings');
                        } else {
                            navigate('/');
                        }
                    } else {
                        navigate('/');
                    }
                } catch {
                    navigate('/');
                }
            } else {
                showAlert('שם משתמש או סיסמה שגויים', 'error');
            }
        } catch {
            showAlert('שגיאת רשת, אנא נסה שוב', 'error');
        } finally {
            setLoading(false);
        }
    };

    // registration availability is no longer shown on the login page (server check happens in register flow)

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const s: SiteSettings = await ApiClient.get('/settings/site/current/settings');
                setSiteSettings(s ?? null);
            } catch (e) {
                setSiteSettings(null);
            }
        };
        loadSettings();
    }, []);

    const showAccountToggles = !!siteSettings && (siteSettings.enablePatientLogin !== false || siteSettings.enableUsersLogin !== false);

    return (
        <div>
            <div className="login-container">
                <form onSubmit={handleSubmit}>
                    <h2>כניסה לאתר</h2>
                    {siteSettings && siteSettings.enableUsersLogin === false && (
                        <div className="login-warning">המערכת מושבתת למשתמשים (login disabled)</div>
                    )}
                    {/* indicate which login type is currently selected */}
                    <div style={{ marginBottom: 8 }}>
                        <strong>מצב כניסה:&nbsp;</strong>
                        <span>{accountType === 'patient' ? 'כניסת מטופל' : 'כניסת צוות/מנהל'}</span>
                    </div>
                    <div className="form-row">
                        <label htmlFor="name">שם משתמש:</label>
                        <input
                            id="name"
                            type="text"
                            value={name}
                            onChange={e => { setName(e.target.value); const err = validateName(e.target.value); setFieldError(err); }}
                            autoComplete="username"
                            aria-describedby={fieldError ? 'name-error' : undefined}
                            aria-invalid={!!fieldError}
                            required
                        />
                        {fieldError ? <div id="name-error" className="error-message">{fieldError}</div> : null}
                    </div>
                    <div className="form-row">
                        <label htmlFor="password">סיסמא:</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                            aria-required="true"
                        />
                    </div>
                    <button className="login-button btn-on-dark" type="submit" disabled={loading || (siteSettings && siteSettings.enableUsersLogin === false)} aria-label="התחבר">{loading ? 'טוען...' : 'התחבר'}</button>
                </form>
            </div>
            <div style={{ marginTop: 8 }}>
                {showAccountToggles && (
                <div className="account-toggle-group">
                    <label
                        className={`toggle-button ${accountType === 'patient' ? 'selected' : ''}`}
                        onClick={() => updateAccountType('patient')}
                        tabIndex={0}
                        role="radio"
                        aria-checked={accountType === 'patient'}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updateAccountType('patient'); } }}
                        onFocus={() => setFocused('patient')}
                        onBlur={() => setFocused(null)}
                    >
                        <input className="sr-only" type="radio" name="accountType" value="patient" checked={accountType === 'patient'} onChange={() => updateAccountType('patient')} />
                        מטופל
                    </label>

                    <label
                        className={`toggle-button ${accountType === 'manager' ? 'selected' : ''}`}
                        onClick={() => updateAccountType('manager')}
                        tabIndex={0}
                        role="radio"
                        aria-checked={accountType === 'manager'}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updateAccountType('manager'); } }}
                        onFocus={() => setFocused('manager')}
                        onBlur={() => setFocused(null)}
                    >
                        <input className="sr-only" type="radio" name="accountType" value="manager" checked={accountType === 'manager'} onChange={() => updateAccountType('manager')} />
                        צוות / מנהל
                    </label>
                </div>)}
                {canRegisterForSelectedType && (
                    <div style={{ marginTop: 8 }}>
                        {/* Only show register when registration is allowed for the selected type */}
                        <button className="btn" onClick={handleRegisterClick}>{registerLabel}</button>
                    </div>
                )}
            </div>
            <div>
                <a href="/forgot-password">שכחת סיסמא?</a>
            </div>
        </div>
    );
};

export default Login;
